import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/constants";
import {
  LayoutDashboard,
  ClipboardList,
  Users,
  Building2,
  Tag,
  UserCog,
  ShieldCheck,
  LogOut,
  Wrench,
  ScanLine,
  PlusCircle,
  DollarSign,
  Coins,
  BarChart3,
  Target,
  Calculator,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  X,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!user) return children;

  const isTech = user.role === "technician";
  const showAdmin = user.role === "admin";

  const primaryLinks = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", show: !isTech },
    { to: "/orders", icon: ClipboardList, label: "Órdenes", show: true },
    { to: "/my-goals", icon: Target, label: "Mis metas", show: isTech },
    { to: "/scanner", icon: ScanLine, label: "Escanear QR", show: true },
    { to: "/customers", icon: Users, label: "Clientes", show: !isTech },
  ];
  const adminLinks = [
    { to: "/admin/users", icon: UserCog, label: "Usuarios" },
    { to: "/admin/roles", icon: ShieldCheck, label: "Roles y permisos" },
    { to: "/admin/branches", icon: Building2, label: "Sucursales" },
    { to: "/admin/categories", icon: Tag, label: "Categorías" },
    { to: "/admin/services", icon: DollarSign, label: "Servicios y Precios" },
    { to: "/admin/costs", icon: Coins, label: "Costos de reparación" },
    { to: "/admin/regularize", icon: Calculator, label: "Regularizar costos" },
    { to: "/admin/goals", icon: Target, label: "Metas de producción" },
    { to: "/admin/reports", icon: BarChart3, label: "Estadísticas" },
    { to: "/admin/settings", icon: SettingsIcon, label: "Configuración" },
  ];

  function handleLogout() {
    logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen text-slate-900">
      {/* Top bar (mobile + desktop) */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3 lg:px-6">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 rounded-xl -mx-1 px-1 py-0.5 transition hover:bg-slate-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            data-testid="header-logo-home"
            title="Ir al inicio"
            aria-label="Ir al inicio"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center shadow-sm">
              <Wrench size={18} className="text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-lg tracking-tight text-slate-900">
                Punto<span className="text-blue-600">Play</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                Workshop OS
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Identificación del usuario — visible SIEMPRE (móvil + desktop) */}
            <div className="flex flex-col items-end leading-tight max-w-[130px] sm:max-w-none" data-testid="topbar-user">
              <div
                className="text-[13px] sm:text-sm font-semibold text-slate-900 truncate"
                data-testid="topbar-user-name"
                title={user.name}
              >
                <span className="hidden xs:inline">Hola, </span>{user.name.split(" ")[0]}
                <span className="hidden sm:inline">{user.name.includes(" ") ? " " + user.name.split(" ").slice(1).join(" ") : ""}</span>
              </div>
              <div
                className="text-[9px] sm:text-[10px] uppercase tracking-widest text-blue-600 font-mono"
                data-testid="topbar-user-role"
              >
                {ROLE_LABEL[user.role]}
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="btn-ghost"
              title="Cerrar sesión"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col w-64 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-61px)] sticky top-[61px] p-4 gap-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2 mt-2 px-2">
            Operación
          </div>
          {primaryLinks
            .filter((l) => l.show)
            .map((l) => (
              <SidebarLink key={l.to} to={l.to} icon={l.icon} label={l.label} />
            ))}
          {showAdmin && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2 mt-4 px-2">
                Administración
              </div>
              {adminLinks.map((l) => (
                <SidebarLink key={l.to} to={l.to} icon={l.icon} label={l.label} />
              ))}
            </>
          )}
          <div className="mt-auto pt-4 border-t border-slate-200">
            <button
              className="btn-primary w-full"
              onClick={() => navigate("/orders/new")}
              data-testid="sidebar-new-order-button"
            >
              <PlusCircle size={18} />
              Nueva Orden
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 pb-24 lg:pb-8 lg:px-8 lg:py-6 px-4 py-4 fade-in-up">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200">
        <div className="grid grid-cols-5">
          {isTech ? (
            <>
              <MobileTab to="/orders" icon={ClipboardList} label="Órdenes" />
              <MobileTab to="/scanner" icon={ScanLine} label="Escanear" highlight />
              <MobileTab to="/orders/new" icon={PlusCircle} label="Nueva" />
              <MobileTab to="/my-goals" icon={Target} label="Metas" />
              <MobileTab to="/dashboard" icon={LayoutDashboard} label="Inicio" />
            </>
          ) : (
            <>
              <MobileTab to="/dashboard" icon={LayoutDashboard} label="Inicio" />
              <MobileTab to="/orders" icon={ClipboardList} label="Órdenes" />
              <MobileTab to="/orders/new" icon={PlusCircle} label="Nueva" highlight />
              <MobileTab to="/scanner" icon={ScanLine} label="QR" />
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                className="flex flex-col items-center justify-center gap-0.5 py-2 text-slate-600 hover:text-blue-600 transition-colors"
                data-testid="mobile-tab-more"
              >
                <MenuIcon size={20} />
                <span className="text-[10px] font-medium">Más</span>
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Mobile "Más" bottom-sheet — admin/supervisor/reception */}
      {!isTech && moreOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end"
          onClick={() => setMoreOpen(false)}
          data-testid="mobile-more-overlay"
        >
          <div
            className="w-full bg-white rounded-t-3xl p-4 pb-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-more-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-extrabold text-slate-900 text-lg">Más opciones</div>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"
                data-testid="mobile-more-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              <MobileMoreLink to="/customers" icon={Users} label="Clientes" onNavigate={() => setMoreOpen(false)} />
              {showAdmin && adminLinks.map((l) => (
                <MobileMoreLink key={l.to} to={l.to} icon={l.icon} label={l.label} onNavigate={() => setMoreOpen(false)} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarLink({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={`sidebar-link-${to.replace(/[/]/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all font-medium ${
          isActive
            ? "bg-blue-50 text-blue-600 border border-blue-200"
            : "text-slate-600 hover:bg-slate-100 hover:text-white border border-transparent"
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );
}

function MobileTab({ to, icon: Icon, label, highlight }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={`mobile-tab-${to.replace(/[/]/g, "-")}`}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 py-3 text-[10px] font-semibold uppercase tracking-wider transition-all ${
          isActive
            ? "text-blue-600"
            : highlight
            ? "text-blue-500"
            : "text-slate-500"
        }`
      }
    >
      <Icon size={22} strokeWidth={isActiveIconStroke()} />
      <span>{label}</span>
    </NavLink>
  );
}
function isActiveIconStroke() {
  return 2.2;
}


function MobileMoreLink({ to, icon: Icon, label, onNavigate }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onNavigate}
      data-testid={`mobile-more-link-${to.replace(/[/]/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-3 rounded-xl transition-all font-medium ${
          isActive
            ? "bg-blue-50 text-blue-700 border border-blue-200"
            : "text-slate-700 hover:bg-slate-100 border border-transparent"
        }`
      }
    >
      <Icon size={18} />
      <span className="text-sm">{label}</span>
    </NavLink>
  );
}
